import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, Checkbox, DateRangePicker, FilterChip, Skeleton, Tag } from '@indipia/ui-core';
import { getSiteColor } from '@indipia/shared';
import { worklogApi } from '../api/worklogApi';
import { useWorklogStore } from '../store/worklogStore';
import { ThumbImg } from '../components/ThumbImg';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { GroupWeather, pickGroupWeather } from '../components/GroupWeather';
import { useFitThumbCount } from '../hooks/useFitThumbCount';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import { useHolidays } from '../hooks/useHolidays';
import { presetRange, DATE_PRESETS, type DatePreset as Preset } from '../config/datePreset';
import { SitePanelProvider } from '../components/site-panel/SitePanelContext';
import { CustReqToggleButton, WorkOrderCustReqList } from '../components/custReq/WorkOrderCustReqList';
import { deliveredList } from '../utils/custReqOrder.logic';
import { SiteArea } from '../components/site-panel/SiteArea';
import { useSiteSelection } from '../components/site-panel/useSiteSelection';
import type { BasicInfoPartner, WorkOrderListItem, WorkOrderWeather } from '../types/worklog.types';

const BASE = '/agents/work-log';
const PAGE_SIZE = 20;

type LoadState = 'loading' | 'ready' | 'error';

// ── 날짜 유틸 ────────────────────────────────────────────────
const pad2 = (n: number) => String(n).padStart(2, '0');
const toYmd = (d: Date) => `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
const toDash = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const DOW = ['일', '월', '화', '수', '목', '금', '토'];
/** 발송일시 ISO → 날짜 키 'YYYY-MM-DD' (일자 그룹핑용) */
function dateKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** ISO → '2026.06.11 (목)' 날짜 그룹 라벨 (공사현황·작업보고 확인과 통일) */
function fmtDateLabel(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} (${DOW[d.getDay()]})`;
}
/** ISO → '2026.10.06 10:34'(24시간) — 카드 '발송일시' */
function fmtSentDtm(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// ── 일자 그룹 — 발송일시(sentAt) 기준. visibleItems 순서를 보존해 정렬(최신/과거순)이 그대로 반영된다. ──
interface DateGroup {
  key: string;
  sample: string; // 대표 sentAt (날짜 라벨용)
  items: WorkOrderListItem[];
  /** 날짜 헤더에 보일 날씨 — 그날 날씨가 있는 가장 이른 작업요청의 스냅샷(요청 시점 기준). 없으면 null */
  weather: WorkOrderWeather | null;
}
function groupByDate(items: WorkOrderListItem[]): DateGroup[] {
  const map = new Map<string, DateGroup>();
  for (const it of items) {
    const dk = dateKey(it.sentAt);
    let dg = map.get(dk);
    if (!dg) {
      dg = { key: dk, sample: it.sentAt, items: [], weather: null };
      map.set(dk, dg);
    }
    dg.items.push(it);
  }
  const groups = [...map.values()];
  for (const dg of groups) dg.weather = pickGroupWeather(dg.items, (it) => it.weather, (it) => it.sentAt);
  return groups;
}

export function WorkOrderListPage() {
  const navigate = useNavigate();

  const [preset, setPreset] = useState<Preset>('7d');
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const holidays = useHolidays();
  // 유효 기간: 직접설정(customRange) 우선, 없으면 프리셋
  const range = customRange
    ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
    : presetRange(preset);
  const [sortDesc, setSortDesc] = useState(true);

  const [selectedPartners, setSelectedPartners] = useState<BasicInfoPartner[]>([]);
  const [partnerPopOpen, setPartnerPopOpen] = useState(false);
  // 반장(수신자) 목록 — 에이전트 진입 시 worklogStore(basic-info)에 적재된 것을 재사용(직접 조회 안 함)
  const storePartners = useWorklogStore((s) => s.partners);
  const worklogStoreStatus = useWorklogStore((s) => s.status);
  const partnersError = worklogStoreStatus === 'error';
  // ①반장(worklogStore) + ②현장(SiteArea 내부 조회) 각각 완료 게이트 → 둘 다 완료 후 ③작업요청 조회
  const partnersLoaded = worklogStoreStatus === 'ready' || worklogStoreStatus === 'error';
  // 로딩 중엔 null → 팝오버가 '불러오는 중입니다.' 표시, 완료 후 배열
  const partners = partnersLoaded ? storePartners : null;
  const [worklogsLoaded, setWorklogsLoaded] = useState(false);
  const baseLoaded = partnersLoaded && worklogsLoaded;

  const [items, setItems] = useState<WorkOrderListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  /** offset 페이지 번호(0-base) — BE offset 페이징. 추가로드 시 +1 */
  const pageRef = useRef(0);
  const [hasNext, setHasNext] = useState(false);
  const [state, setState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  // 무한스크롤 — 스크롤 컨테이너 + 하단 센티넬(공사현황과 동일)
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // 카드 보기모드(false=1열, true=2열). 공사현황·현장보고 목록과 동일한 보기모드 토글.
  // 화면 오픈 시 기본 1열보기
  const [twoCol, setTwoCol] = useState(false);
  // 현장목록 공통 선택 규칙(REQ-C-01, 토글 단일선택) — 3개 목록 화면 공용 훅
  const { selectedSiteId, isSiteSelected, toggleSite, selectOnlySite, onSitesLoaded: handleSitesLoaded } = useSiteSelection();

  const partnerIds = selectedPartners.map((p) => p.partnerId);
  // 의존성 배열용 안정 키
  const partnerKey = partnerIds.join(',');

  // 반장 목록은 worklogStore(basic-info)에서 파생 — 이 화면에서 별도 조회하지 않는다.

  const fetchList = useCallback(
    (opts: { append: boolean }, signal?: AbortSignal) => {
      const { from, to } = customRange
        ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
        : presetRange(preset);
      const page = opts.append ? pageRef.current + 1 : 0;
      return worklogApi
        .getWorkOrders(
          {
            sentDateFrom: toYmd(from),
            sentDateTo: toYmd(to),
            partnerId: partnerIds.length ? partnerIds : undefined,
            page,
            size: PAGE_SIZE,
            sort: sortDesc ? '-createdAt' : 'createdAt',
          },
          signal,
        )
        .then((res) => {
          const data = res.data.data;
          setItems((prev) => (opts.append ? [...prev, ...data.items] : data.items));
          setTotalCount(data.totalCount);
          setHasNext(data.hasNext);
          pageRef.current = page;
          setState('ready');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preset, partnerKey, sortDesc, customRange],
  );

  // ③ ①·②(baseLoaded) 완료 후 작업요청 목록 조회 — 이후 필터(기간·수신자·정렬) 변경 시에도 재조회
  useEffect(() => {
    if (!baseLoaded) return;
    const controller = new AbortController();
    setState('loading');
    fetchList({ append: false }, controller.signal).catch((err) => {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      setState('error');
    });
    return () => controller.abort();
  }, [baseLoaded, fetchList]);

  const loadMore = useCallback(() => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    fetchList({ append: true })
      .catch(() => {/* 추가 조회 실패는 조용히 무시 (기존 목록 유지) */})
      .finally(() => setLoadingMore(false));
  }, [hasNext, loadingMore, fetchList]);

  // 무한스크롤 — 하단 센티넬이 스크롤 뷰에 들어오면 자동으로 다음 페이지 조회(공사현황과 동일).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || state !== 'ready' || !hasNext || loadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [state, hasNext, loadingMore, loadMore]);

  // 현장 패널에서 해제된 현장의 작업요청은 결과에서 제외
  const visibleItems = items.filter((it) => isSiteSelected(it.siteId));
  const groups = groupByDate(visibleItems);
  const partnerLabel =
    selectedPartners.length === 0
      ? '+ 수신자'
      : selectedPartners.length === 1
        ? selectedPartners[0].partnerName
        : `${selectedPartners[0].partnerName} 외 ${selectedPartners.length - 1}`;

  return (
    <SitePanelProvider>
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col bg-surface">
      {/* ── 조회조건 바 ── */}
      <div className="flex flex-wrap items-center gap-sm border-b border-border px-06 py-04">
        {/* 기간 직접 설정 — 옆의 프리셋 칩보다 우선한다(active 로 어느 쪽이 이겼는지 표시) */}
        <DateRangePicker
          value={{ start: range.from, end: range.to }}
          onChange={({ start, end }) => {
            if (start && end) setCustomRange({ from: toDash(start), to: toDash(end) });
          }}
          active={!!customRange}
          holidays={holidays}
          presets={false}
          aria-label="기간 직접 설정"
        />

        {/* 기간 프리셋 (공통 — 현장보고·공사현황과 통일) */}
        {DATE_PRESETS.map(([key, label]) => (
          <FilterChip
            key={key}
            active={!customRange && preset === key}
            onClick={() => {
              setCustomRange(null); // 프리셋 선택 시 직접설정 해제
              setPreset(key);
            }}
          >
            {label}
          </FilterChip>
        ))}

        <div className="mx-1 h-5 w-px bg-border" />

        {/* 수신자(반장) 필터 */}
        <div className="relative">
          <FilterChip active={selectedPartners.length > 0} onClick={() => setPartnerPopOpen((v) => !v)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            {partnerLabel}
          </FilterChip>
          {partnerPopOpen && (
            <PartnerFilterPopover
              selected={selectedPartners}
              partners={partners}
              error={partnersError}
              onClose={() => setPartnerPopOpen(false)}
              onApply={(list) => {
                setSelectedPartners(list);
                setPartnerPopOpen(false);
              }}
            />
          )}
        </div>
      </div>

      {/* ── 툴바 ── */}
      <div className="flex items-center justify-between gap-sm px-06 py-04">
        <div className="flex items-baseline gap-02">
          <b className="text-heading-md font-bold text-text">{visibleItems.length}</b>
          <span className="text-body-sm font-medium text-text-muted">/ {totalCount}건</span>
        </div>
        <div className="flex items-center gap-sm">
          {/* 정렬 — 화면기획서 실측: 2지선다 Segmented가 아니라 아이콘·라벨이 바뀌는 단일 토글 버튼 */}
          <Button variant="etc" size="md" onClick={() => setSortDesc((v) => !v)} aria-label="정렬">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M12 5v14" />
              {sortDesc ? <path d="M5 12l7-7 7 7" /> : <path d="M19 12l-7 7-7-7" />}
            </svg>
            {sortDesc ? '최신순' : '과거순'}
          </Button>
          {/* 보기모드 토글(1열/2열) — 공사현황·현장보고 목록과 동일 아이콘 */}
          <Button
            variant="etc"
            size="md"
            onClick={() => setTwoCol((v) => !v)}
            aria-pressed={twoCol}
            aria-label={twoCol ? '1열 보기' : '2열 보기'}
          >
            {twoCol ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            )}
            {twoCol ? '1열 보기' : '2열 보기'}
          </Button>
          <Button size="md" onClick={() => navigate(`${BASE}/workorder/new`)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            작업요청 작성
          </Button>
        </div>
      </div>

      {/* ── 목록 ── */}
      <div ref={scrollRef} className="flex-1 overflow-auto px-06 pb-08">
        {state === 'loading' && (
          <div className="space-y-04">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={112} className="rounded-lg" />
            ))}
          </div>
        )}

        {state === 'error' && (
          <div className="mt-06 flex flex-col items-center gap-04 rounded-lg border border-border bg-surface-raised py-10">
            <span className="text-body-sm text-text-secondary">작업요청을 불러오지 못했습니다.</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // 재조회: 즉시 로딩 표시 후 작업요청 목록 재조회. 실패 시 다시 error.
                setState('loading');
                fetchList({ append: false }).catch(() => setState('error'));
              }}
            >
              다시 조회
            </Button>
          </div>
        )}

        {/* 전체 해제 상태(REQ-C-01) — 필터 결과 없음과는 다른 전용 안내(화면기획서 실측) */}
        {state === 'ready' && selectedSiteId == null && (
          <div className="mt-06 flex flex-col items-center gap-md py-11 text-center">
            <div className="flex h-09 w-09 items-center justify-center rounded-lg bg-surface">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="text-text-muted">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="text-body-md font-bold text-text-secondary">오른쪽 현장목록에서 현장을 먼저 선택하세요</div>
          </div>
        )}

        {state === 'ready' && selectedSiteId != null && visibleItems.length === 0 && (
          <div className="mt-06 flex flex-col items-center gap-sm rounded-lg border border-border bg-surface-raised py-11 text-center">
            <div className="text-heading-sm font-bold text-text">작성된 작업요청이 없습니다</div>
            <div className="text-body-md leading-relaxed text-text-secondary">
              선택한 기간에 발송된 작업요청이 없습니다.
              <br />
              '작업요청 작성'을 눌러 작업요청을 작성해 보세요.
            </div>
          </div>
        )}

        {state === 'ready' && groups.length > 0 && (
          <div className="space-y-05">
            {groups.map((dg) => (
              <div key={dg.key}>
                {/*
                  날짜 그룹 라벨 — 공사현황·작업보고 확인과 동일. 날짜 옆에 그날 첫 작업요청의 날씨(요청 시점 기준)를 보인다.
                  스크롤해도 목록 상단에 고정되고, 다음 날짜 헤더가 올라오면 이전 날짜를 밀어낸다(화면기획서 v1.8).
                  그룹 래퍼 안에 두어 그룹이 끝나면 헤더도 함께 빠진다.
                  z-10: z-sticky(1100)는 z-dropdown(1000)보다 높아 툴바 팝오버(수신자 필터)를 가려서 쓰지 않는다.
                  고정 박스의 위·아래 패딩(py-03)이 카드와의 간격을 겸한다(카드가 헤더 밑으로 비쳐 보이지 않게).
                */}
                <div className="sticky top-0 z-10 flex items-center gap-03 bg-surface py-03">
                  <span className="text-body-md font-bold text-text">{fmtDateLabel(dg.sample)}</span>
                  {dg.weather && <GroupWeather weather={dg.weather} />}
                  <span className="h-px flex-1 bg-border" />
                </div>
                <div className={twoCol ? 'grid grid-cols-1 gap-04 lg:grid-cols-2' : 'grid grid-cols-1 gap-04'}>
                  {dg.items.map((it) => (
                    <WorkOrderCard key={it.workOrderId} item={it} />
                  ))}
                </div>
              </div>
            ))}

            {/* 무한스크롤 센티넬 — 뷰에 들어오면 자동 조회 */}
            {hasNext && (
              <div ref={sentinelRef} className="flex items-center justify-center py-05 text-body-sm text-text-muted">
                {loadingMore ? '불러오는 중입니다.' : ''}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
      {/* ── 우측 공사현장 목록 (플랫폼 현장패널 구조 복제, worklog 데이터) ── */}
      <SiteArea
        onFirstLoad={() => setWorklogsLoaded(true)}
        isSiteSelected={isSiteSelected}
        onToggleSite={toggleSite}
        onSitesLoaded={handleSitesLoaded}
        onSiteRegistered={selectOnlySite}
        onSiteDeleted={() => {
          // 삭제된 현장의 작업요청이 이미 조회해 둔 목록에 남아있으므로 재조회로 반영한다.
          setState('loading');
          fetchList({ append: false }).catch(() => setState('error'));
        }}
      />
    </div>
    </SitePanelProvider>
  );
}

// ── 작업요청 카드 — 작업보고 확인(FieldReportListPage ReportCard)과 같은 카드 스타일 ──
// 헤더(작업자명 굵게 + 공종 배지) / 현장·고객 메타 / 발송일시·참조인 / 본문 1줄 클램프 + 더보기 / 사진(카드 너비만큼 + "+N 더보기").
// 열람·확인 상태나 클릭 동작은 없어(조회 전용) 카드는 상호작용 요소가 아니다.
function WorkOrderCard({ item }: { item: WorkOrderListItem }) {
  const [lbIdx, setLbIdx] = useState<number | null>(null);
  const [textExpanded, setTextExpanded] = useState(false);
  const [photosExpanded, setPhotosExpanded] = useState(false);
  // 전달 당시 고객 요청 목록 펼침(S16-9)
  const [custReqOpen, setCustReqOpen] = useState(false);
  const custReqs = deliveredList(item.customerRequests);

  // 본문이 1줄 클램프로 넘치는지 — CSS만으로는 JS에서 알 수 없어 scrollHeight/clientHeight로 판정.
  const textRef = useRef<HTMLParagraphElement>(null);
  const [textOverflowing, setTextOverflowing] = useState(false);
  useEffect(() => {
    const el = textRef.current;
    if (el) setTextOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [item.orderContent]);

  const worker = item.recipients.find((r) => r.role === 'WORKER');
  const ccs = item.recipients.filter((r) => r.role === 'CC');

  // 썸네일 노출 개수(REQ-1-22, 화면기획서 v1.8) — 카드 너비에 들어가는 만큼만 보이고, 넘치면 마지막 칸을 "+N 더보기" 타일로 쓴다.
  // 1열↔2열·창 크기가 바뀌면 개수도 다시 계산한다(useFitThumbCount). 펼치면 전부 + "접기"(칸을 넘길 때만).
  const total = item.photos.length;
  const { ref: thumbsRef, fit } = useFitThumbCount<HTMLDivElement>(64, 8, total > 0);
  const overflowing = total > fit;
  const collapsed = !photosExpanded && overflowing;
  const shown = collapsed ? item.photos.slice(0, fit - 1) : item.photos;
  const hidden = total - shown.length;

  return (
    <article className="overflow-hidden rounded-lg border border-border bg-surface-raised">
      {/* 헤더 — 작업자명(굵게) + 공종 배지, 아래 줄에 현장·고객 */}
      <div className="p-05 pb-04">
        <div className="flex flex-wrap items-center gap-02">
          <span className="text-body-md font-bold text-text">{worker?.partnerName ?? '-'}</span>
          <Badge status="info">{item.workTypeName}</Badge>
          <CustReqToggleButton count={custReqs.length} open={custReqOpen} onToggle={() => setCustReqOpen((v) => !v)} />
        </div>
        <div className="mt-01 flex items-center gap-02 text-caption text-text-muted">
          <span className="inline-flex min-w-0 items-center gap-02">
            <span className="h-03 w-03 shrink-0 rounded-full" style={{ background: getSiteColor(item.siteId) }} />
            <span className="truncate">{item.siteName}</span>
          </span>
          <span className="text-border">|</span>
          <span className="shrink-0">{item.customerName}</span>
        </div>
      </div>

      {custReqOpen && <WorkOrderCustReqList items={custReqs} />}

      {/* 발송일시 · 참조인 — 본문 위(E 공사현황·C-3 이력·D 작업보고 확인 카드와 같은 표기). 라벨 연하게·값 진하게, 값 시작 위치 정렬 */}
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-sm gap-y-sm px-05 pb-03 text-caption">
        <dt className="text-text-muted">발송일시:</dt>
        <dd className="font-medium text-text">{fmtSentDtm(item.sentAt)}</dd>
        {ccs.length > 0 && (
          <>
            <dt className="text-text-muted">참조인:</dt>
            <dd className="font-medium text-text">{ccs.map((r) => r.partnerName).join(', ')}</dd>
          </>
        )}
      </dl>

      {/* 본문 — 1줄 클램프 + 실제로 넘칠 때만 더보기/접기(REQ-1-22·23) */}
      <p
        ref={textRef}
        className={`px-05 text-body-sm leading-relaxed text-text-secondary ${(textOverflowing || textExpanded) ? 'pb-02' : 'pb-05'}`}
        style={
          textExpanded
            ? { whiteSpace: 'pre-line' }
            : { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-line' }
        }
      >
        {item.orderContent}
      </p>
      {(textOverflowing || textExpanded) && (
        <button
          type="button"
          onClick={() => setTextExpanded((v) => !v)}
          className="px-05 pb-04 text-caption font-bold text-info"
        >
          {textExpanded ? '접기' : '+더보기'}
        </button>
      )}

      {total > 0 && (
        <div className="px-05 pb-05">
          <div ref={thumbsRef} className="flex flex-wrap gap-sm">
            {shown.map((p, i) => (
              <button
                key={i}
                type="button"
                aria-label={`사진 ${i + 1} 크게보기`}
                onClick={() => setLbIdx(i)}
                className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-surface-muted p-0"
              >
                <ThumbImg url={p.thumbUrl} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
            {collapsed && (
              <button
                type="button"
                aria-label={`사진 ${hidden}장 더 보기`}
                onClick={() => setPhotosExpanded(true)}
                className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md border border-dashed text-caption font-bold leading-tight"
                style={{ background: 'var(--color-infoSubtle)', borderColor: 'var(--status-info-200)', color: 'var(--color-info)' }}
              >
                +{hidden}
                <span>더보기</span>
              </button>
            )}
          </div>
          {photosExpanded && overflowing && (
            <button
              type="button"
              onClick={() => setPhotosExpanded(false)}
              className="mt-02 text-caption font-bold text-info"
            >
              접기
            </button>
          )}
        </div>
      )}

      {/* 크게보기 — 인증 필요(사장님 화면)라 blob 로더(ThumbImg)로 렌더. */}
      <PhotoLightbox
        photos={item.photos.map((p) => ({ url: p.url }))}
        index={lbIdx}
        onClose={() => setLbIdx(null)}
        onIndexChange={setLbIdx}
        renderImage={(photo, style) => (
          <ThumbImg url={photo.url} alt="" imgStyle={style} />
        )}
      />
    </article>
  );
}

// ── 수신자(반장) 필터 팝오버 ─────────────────────────────────
/** 팝오버 행에서 바로 보여주는 공종 칩 최대 개수 — 초과분은 '+N' */
const MAX_TRADE_CHIPS = 3;

function PartnerFilterPopover({
  selected,
  partners,
  error,
  onApply,
  onClose,
}: {
  selected: BasicInfoPartner[];
  partners: BasicInfoPartner[] | null;
  error: boolean;
  onApply: (list: BasicInfoPartner[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<number>>(new Set(selected.map((p) => p.partnerId)));

  // 공종 코드 → 공종명 매핑 (공통코드 ZEU_WORK_TYPE)
  const workTypeName = useWorkTypeName();

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => onApply((partners ?? []).filter((p) => checked.has(p.partnerId)));

  return (
    <>
      {/* 바깥 클릭 닫기 */}
      {/* 팝오버(.menu = z-dropdown)와 같은 z-dropdown 이어야 한다 — 더 높은 z-overlay 면 투명 오버레이가 팝오버를 덮어
          팝오버 안의 클릭(체크·적용)과 휠 스크롤이 전부 오버레이로 가서 필터가 동작하지 않았다(다른 필터 팝오버와 동일). */}
      <div className="fixed inset-0 z-dropdown" onClick={onClose} aria-hidden />
      <div className="menu show absolute left-0 top-full mt-02 w-64">
        <div className="mb-03 flex items-center justify-between">
          <span className="text-body-md font-semibold text-text">수신자 선택</span>
          <Button variant="ghost" size="sm" onClick={() => setChecked(new Set())}>
            전체 해제
          </Button>
        </div>
        {/* 가로 스크롤 없음(overflow-x-hidden) — 칩이 많아도 목록 폭 안에서 줄바꿈한다 */}
        <div className="max-h-56 space-y-01 overflow-y-auto overflow-x-hidden">
          {partners === null && !error && (
            <div className="py-06 text-center text-body-sm text-text-muted">불러오는 중입니다.</div>
          )}
          {error && <div className="py-06 text-center text-body-sm text-text-muted">반장 목록을 불러오지 못했습니다.</div>}
          {partners?.length === 0 && (
            <div className="py-06 text-center text-body-sm text-text-muted">등록된 반장이 없습니다.</div>
          )}
          {partners?.map((p) => (
            // 행 레이아웃: [체크박스][아바타][이름 / 공종 칩(아래로 줄바꿈)]. 공종이 많은 반장도 이름이 맨 위에 고정돼
            // 보인다(예전엔 칩을 이름 옆 좁은 열에 두어, 칩이 한 줄씩 쌓이며 행이 길어지고 세로 중앙의 이름·체크박스가 가려졌다).
            // .menu-item 의 align-items:center 를 !items-start 로 덮어 위쪽 기준으로 맞춘다.
            <label key={p.partnerId} className="menu-item !items-start">
              {/* 행 전체가 <label> 이라 bare — Checkbox 자체 label 래핑을 끄고 input만 렌더(label 중첩 방지).
                  h-7(=아바타 높이)로 감싸 첫 줄(아바타·이름) 중심에 맞춘다 */}
              <span className="flex h-7 shrink-0 items-center">
                <Checkbox bare checked={checked.has(p.partnerId)} onChange={() => toggle(p.partnerId)} />
              </span>
              {/* 성(첫글자) 아바타 */}
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-body-sm font-bold text-white"
                style={{ background: getSiteColor(p.partnerId) }}
              >
                {p.partnerName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex h-7 items-center text-body-md font-medium text-text">{p.partnerName}</div>
                {/* 전문분야(취급공정) 칩 — 이름 아래에서 줄바꿈. 공종이 많은 반장은 앞 MAX_TRADE_CHIPS개만 보이고
                    나머지는 '+N'으로 접는다(한 반장 행이 목록 높이를 다 차지하지 않도록). 전체는 hover(title)로 확인. */}
                {p.workTypeCodes && (() => {
                  const names = p.workTypeCodes.split(',').map((code) => workTypeName.get(code) ?? code);
                  const rest = names.length - MAX_TRADE_CHIPS;
                  return (
                    <div className="flex flex-wrap gap-02 pb-01" title={names.join(', ')}>
                      {names.slice(0, MAX_TRADE_CHIPS).map((name, i) => (
                        <Tag key={`${name}-${i}`} size="sm">
                          {name}
                        </Tag>
                      ))}
                      {rest > 0 && <Tag size="sm">+{rest}</Tag>}
                    </div>
                  );
                })()}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-04 flex justify-end gap-sm">
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" onClick={apply}>
            적용
          </Button>
        </div>
      </div>
    </>
  );
}
